import { useEffect, useRef } from 'react';

import { Image360 } from '@shared-components/Image360/Image360';
import VideoIframe from '@shared-components/VideoIframe/VideoIframe';

import { IMAGES_360_TAB_NAME, IMAGES_TAB_NAME, VIDEO_TAB_NAME } from '../Gallery.constants';
import type { GalleryProps, PatchworkGalleryProps } from '../Gallery.entity';
import { GalleryTabs } from '../GalleryTabs';
import { PatchworkGalleryImage } from './PatchworkGalleryImage';
import { PatchworkGalleryPreloadLinks } from './PatchworkGalleryPreloadLinks';

import styles from './PatchworkGallery.module.css';

type Props = PatchworkGalleryProps & Pick<GalleryProps, 'tenant'>;

const PatchworkGallery = ({
  images,
  handleImageClick,
  currentIndex,
  video,
  activeTab,
  setActiveTab,
  hasVideo,
  has360Images,
  images360,
  tabStrings,
  galleryStrings,
  tenant,
}: Props) => {
  const currentImageRef = useRef<HTMLLIElement>(null);

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'Enter') {
      handleImageClick(index);
    }
  };

  useEffect(() => {
    document.body.classList.add('lockScroll');
    currentImageRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    return () => document.body.classList.remove('lockScroll');
  }, []);

  return (
    <>
      {activeTab === VIDEO_TAB_NAME && (
        <div className={styles.videoWrapper}>
          <VideoIframe title="Vehicle video" video={video} />
        </div>
      )}
      {activeTab === IMAGES_360_TAB_NAME && images360 && (
        <Image360 images={images360} galleryStrings={galleryStrings} />
      )}

      {activeTab === IMAGES_TAB_NAME && (
        <ul className={styles.imageWrapper} data-test-id="patchwork-gallery">
          <PatchworkGalleryPreloadLinks
            images={images}
            currentIndex={currentIndex}
            tenant={tenant}
          />
          {images.map((image, index) => {
            const refProp = index === currentIndex ? { ref: currentImageRef } : {};

            return (
              <li
                key={image.url}
                tabIndex={0}
                onClick={() => handleImageClick(index)}
                onKeyDown={handleKeyDown(index)}
                aria-label={`images-of-${index}`}
                {...refProp}
              >
                <PatchworkGalleryImage
                  image={image}
                  className={styles.image}
                  isCurrent={index === currentIndex}
                  tenant={tenant}
                />
              </li>
            );
          })}
        </ul>
      )}
      <GalleryTabs
        activeTab={activeTab}
        switchTab={setActiveTab}
        strings={tabStrings}
        hasVideo={hasVideo}
        has360Images={has360Images}
      />
    </>
  );
};

export default PatchworkGallery;
